import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AccessibilityInfo,
  ActivityIndicator,
  Animated,
  AppState,
  Easing,
  Image,
  Pressable,
  StatusBar,
  Text,
  View,
} from 'react-native';
import { Camera, useCameraDevice, useCameraPermission } from 'react-native-vision-camera';
import { Glyph, TICK } from '~/components/decision';
import { Screen } from '~/components/Screen';
import { Notice, OutlineButton, PrimaryButton, TextButton } from '~/components/ui';
import type { GlassArea } from '~/lib/api';
import { AREA_LABELS } from '~/lib/areas';
import { prepareImage } from '~/lib/image';
import { logger } from '~/lib/logger';
import {
  nextShotFor,
  type Photo,
  photosForArea,
  SHOT_LABEL,
  SHOT_PROMPT,
  shotsFor,
  shotsTaken,
} from '~/lib/photos';
import { motion } from '~/theme';
import { useTheme } from '~/theme/ThemeProvider';

export interface ShootScreenProps {
  areas: GlassArea[];
  photos: Photo[];
  onAdd: (photo: Photo) => void;
  onRemove: (id: string) => void;
  /** Called once every area has at least one photo and the operator moves on. */
  onDone: () => void;
  onReview: () => void;
  onBack: () => void;
}

/**
 * Step 3: photograph each area, one at a time.
 *
 * A sequence, not a list of rows. Walking around a car, "pick a row, shoot, return to
 * the list, find the next row" is the wrong shape — this advances by itself and shows
 * "Area 2 of 3" so the operator knows where they are without counting.
 *
 * Camera lifecycle is the same discipline as the VIN scanner: `isActive` follows app
 * foreground and does NOT include `busy`, because deactivating on the render that starts
 * a capture closes the session takePhoto is about to use.
 *
 * This is the ONE dark screen in the app. Not a second theme — the viewfinder is an image
 * and a light chrome around it both washes it out and makes the operator's eyes adapt to
 * the wrong thing between the glass and the phone. The controls that carry a sentence sit
 * on white cards on top of it, so every word is still the ink-on-white pair.
 */
/**
 * Whether the operating system has been asked to reduce motion.
 *
 * Read live, not once: it is a system setting an operator can change while the app is
 * open, and someone turning it on mid-journey is doing so because something is moving.
 */
function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    let alive = true;
    void AccessibilityInfo.isReduceMotionEnabled().then((v) => {
      if (alive) setReduce(v);
    });
    const sub = AccessibilityInfo.addEventListener('reduceMotionChanged', setReduce);
    return () => {
      alive = false;
      sub.remove();
    };
  }, []);
  return reduce;
}

/**
 * The auto-advance bar: a 4 px track that fills over `motion.autoAdvance`.
 *
 * It is the ONE animation in this screen. Under reduced motion it is drawn static and
 * full instead — the countdown is still running and the "Stay" button beside it is still
 * the way out, so nothing is lost but the movement. A viewfinder that animates is also a
 * viewfinder that makes people wait to see whether it has settled.
 */
function AutoAdvanceBar() {
  const { c, radius } = useTheme();
  const reduceMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(reduceMotion ? 1 : 0)).current;

  useEffect(() => {
    if (reduceMotion) return;
    const anim = Animated.timing(progress, {
      toValue: 1,
      duration: motion.autoAdvance.ms,
      easing: Easing.linear,
      // Width cannot be driven on the UI thread, and a 3.2 s linear fill on the JS
      // thread is not what drops frames here — taking a photograph is.
      useNativeDriver: false,
    });
    anim.start();
    return () => anim.stop();
  }, [progress, reduceMotion]);

  return (
    <View style={{ height: 4, backgroundColor: c.s3 }}>
      <Animated.View
        testID="auto-advance-bar"
        style={{
          height: 4,
          borderRadius: radius.sm,
          backgroundColor: c.brandPrimary,
          width: progress.interpolate({ inputRange: [0, 1], outputRange: ['0%', '100%'] }),
        }}
      />
    </View>
  );
}

/** A camera body, for the shot being asked for; an open ring for one still to come. */
const CAMERA = 'M2 5.5h3l1.5-2h3l1.5 2h3v7H2zM6 9a2 2 0 1 0 4 0a2 2 0 1 0-4 0';
const RING = 'M8 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10z';

export function ShootScreen({
  areas,
  photos,
  onAdd,
  onRemove,
  onDone,
  onReview,
  onBack,
}: ShootScreenProps) {
  const { c, t, radius, space, touch } = useTheme();
  const [index, setIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Operator-declared: is something of known size in frame?
   *
   * Declared rather than detected because nothing on device can see it — the model
   * reports `scaleReference` back, but only after the verdict, far too late to prompt a
   * better photograph. Damage SIZE decides repair versus replace, so this is the single
   * most consequential thing the capture screen can influence.
   */

  /** The last photo taken, while it can still be undone. */
  const [undo, setUndo] = useState<Photo | null>(null);
  /** True once this area is complete and the screen is counting down to the next. */
  const [advancing, setAdvancing] = useState(false);
  const [appActive, setAppActive] = useState(
    AppState.currentState !== 'background' && AppState.currentState !== 'inactive',
  );

  const cameraRef = useRef<Camera>(null);
  const { hasPermission, requestPermission } = useCameraPermission();
  const device = useCameraDevice('back');

  const area = areas[index];
  const shots = area ? photosForArea(photos, area) : [];
  /*
    The shutter fills the next NAMED slot rather than appending a loose frame. That is
    what lets the server's `retake_shot` point at something, and it turns the scale
    reference from a checkbox the operator has to remember into a shot they are asked for.
  */
  const nextShot = area ? nextShotFor(photos, area) : null;
  const taken = area ? shotsTaken(photos, area) : new Set<string>();
  /*
    Two slots on a tempered pane, four on a laminated one. Not a server rule — the API
    accepts all four anywhere — but depth and real-world size feed nothing a tempered
    verdict reads, and every extra image is billed against a 10c journey. See shotsFor.
  */
  const slots = area ? shotsFor(area) : [];
  const canAdd = nextShot !== null;

  useEffect(() => {
    const sub = AppState.addEventListener('change', (n) => setAppActive(n === 'active'));
    return () => sub.remove();
  }, []);
  useEffect(() => {
    if (!hasPermission) void requestPermission();
  }, [hasPermission, requestPermission]);

  // Deliberately excludes `busy`: including it deactivates the camera on the render that
  // starts a capture, and takePhoto then fails with "Camera is closed."
  const cameraActive = hasPermission && appActive;

  const shoot = useCallback(async () => {
    logger.log('[Shoot] shutter', { area, hasCamera: !!cameraRef.current, busy });
    if (!cameraRef.current || busy || !area || !nextShot) return;
    setBusy(true);
    setError(null);
    try {
      const photo = await cameraRef.current.takePhoto({});
      const uri = /^(blob:|data:|file:)/.test(photo.path) ? photo.path : `file://${photo.path}`;
      const prepared = await prepareImage(uri);
      const captured: Photo = {
        id: `${area}-${nextShot}-${Date.now()}`,
        area,
        kind: 'area',
        shot: nextShot,
        uri: prepared.uri,
        base64: prepared.base64,
        bytes: prepared.bytes,
        // Derived, not declared: the scale shot IS the scale reference.
        hasScaleRef: nextShot === 'scale',
      };
      onAdd(captured);
      // The shot just taken was the area's last, so start the countdown. Recomputed from
      // `photos` rather than the render-scoped `taken`, so the callback has one source.
      const already = shotsTaken(photos, area);
      if (shotsFor(area).every((sh) => sh === nextShot || already.has(sh))) {
        setAdvancing(true);
      }
    } catch (e) {
      logger.error('[Shoot] capture failed', e);
      setError('Could not take that photo. Try again.');
    } finally {
      setBusy(false);
    }
  }, [area, busy, nextShot, onAdd, photos]);

  const advance = useCallback(() => {
    setAdvancing(false);
    setUndo(null);
    if (index + 1 < areas.length) setIndex(index + 1);
    else onDone();
  }, [index, areas.length, onDone]);

  /*
    Auto-advance: once the area's last slot is filled, move on by itself after
    `motion.autoAdvance`. Walking a car is two hands and a phone, and the operator who
    just finished a pane is already moving to the next one.

    "Stay" cancels it, and so does anything else that changes the area's state — the
    effect is keyed on `advancing`, which only `shoot` sets.
  */
  useEffect(() => {
    if (!advancing) return;
    const id = setTimeout(advance, motion.autoAdvance.ms);
    return () => clearTimeout(id);
  }, [advancing, advance]);

  /*
    The undo window. Deleting is one tap on a 24 pt dot at the edge of a thumbnail, held
    in one hand, outdoors — the easiest mis-tap on the screen, and the photograph is gone
    for good once the window closes.
  */
  useEffect(() => {
    if (!undo) return;
    const id = setTimeout(() => setUndo(null), motion.toastHold.ms);
    return () => clearTimeout(id);
  }, [undo]);

  if (!area) return null;

  return (
    <Screen style={{ backgroundColor: c.inverse }}>
      {/* The app is light; this screen is not. */}
      <StatusBar barStyle="light-content" />
      <View style={{ paddingHorizontal: space.md, gap: space.xs }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.sm }}>
          <Pressable
            testID="shoot-close"
            accessibilityLabel="Leave capture"
            onPress={onBack}
            accessibilityRole="button"
            hitSlop={12}
            style={{
              width: touch.min,
              height: touch.min,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.onInverse }]}>‹</Text>
          </Pressable>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={[t.monoEyebrow, { color: c.onInverseMuted, textTransform: 'uppercase' }]}>
              {`area ${index + 1} of ${areas.length}`}
            </Text>
            <Text style={[t.bodyLg, { fontWeight: '600', color: c.onInverse }]}>
              {AREA_LABELS[area]}
            </Text>
          </View>
          <TextButton testID="go-to-review" label="Review" onPress={onReview} onDark />
        </View>
        {/*
          One pill per named shot: done, the one being asked for, and still to come. The
          frame puts the four here, and the API has modelled the same four since 0014.
        */}
        <View style={{ flexDirection: 'row', gap: space.sm }}>
          {slots.map((shot) => {
            const done = taken.has(shot);
            const now = nextShot === shot;
            return (
              <View
                key={shot}
                testID={`shot-${shot}`}
                accessibilityLabel={`${SHOT_LABEL[shot]}: ${done ? 'taken' : now ? 'next' : 'to do'}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: space.xs,
                  paddingHorizontal: space.sm,
                  height: 32,
                  borderRadius: radius.pill,
                  borderWidth: done || now ? 0 : 1,
                  borderColor: c.inverseRule,
                  backgroundColor: done ? c.inverseRaise : now ? c.surface : 'transparent',
                }}
              >
                <Glyph
                  path={done ? TICK : now ? CAMERA : RING}
                  size={14}
                  color={done ? c.onInverse : now ? c.text : c.onInverseMuted}
                />
                <Text
                  style={[t.label, { color: done ? c.onInverse : now ? c.text : c.onInverseMuted }]}
                >
                  {SHOT_LABEL[shot]}
                </Text>
              </View>
            );
          })}
        </View>

        {/*
          What to do, for the shot being asked for. It replaced a toggle the operator had
          to remember to set before the scale shot — a checkbox standing between the
          product's most consequential input and the person least able to remember it.
        */}
        {nextShot ? (
          <Text style={[t.body, { color: c.onInverse }]}>
            <Text style={{ fontWeight: '600' }}>{SHOT_LABEL[nextShot]}</Text>
            {` · ${SHOT_PROMPT[nextShot]}`}
          </Text>
        ) : (
          <Text style={[t.body, { color: c.onInverseMuted }]}>
            All four shots. Tap a photo to check it, or move on.
          </Text>
        )}
      </View>

      <View
        style={{
          flex: 1,
          margin: space.md,
          borderRadius: radius.lg,
          overflow: 'hidden',
          backgroundColor: c.inverseRaise,
        }}
      >
        {hasPermission && device ? (
          <Camera
            ref={cameraRef}
            style={{ flex: 1 }}
            device={device}
            isActive={cameraActive}
            photo
            onError={(e) => {
              logger.error('[Shoot] camera error', e.code, e.message);
              setError(
                e.code === 'device/camera-already-in-use'
                  ? 'Another app is using the camera. Close it and try again.'
                  : `Camera problem (${e.code}).`,
              );
            }}
          />
        ) : (
          <View
            style={{ flex: 1, alignItems: 'center', justifyContent: 'center', padding: space.lg }}
          >
            <Text style={[t.body, { color: c.onInverse, textAlign: 'center' }]}>
              {hasPermission ? 'No camera found.' : 'Camera access is needed to photograph glass.'}
            </Text>
          </View>
        )}
        {/* Reticle plus a dashed card outline, so the scale reference is asked for
            visually and not only in words. */}
        <View
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            margin: space.lg,
            borderWidth: 2,
            borderColor: c.onInverseMuted,
            borderRadius: radius.lg,
            alignItems: 'center',
            justifyContent: 'center',
            gap: space.sm,
          }}
        >
          {/*
            The card outline is drawn at the real 85.6 x 54 mm ratio and never moves. It is
            the only thing in the frame that makes a length measurable, so it is present
            whether or not the operator has said a card is there.
          */}
          {/*
            Only on the scale shot. The frame draws the outline where the card goes, and a
            card-shaped box floating in the frame while the operator is asked for a wide
            shot is an instruction for a different moment.
          */}
          {nextShot === 'scale' ? (
            <>
              <View
                style={{
                  width: 172,
                  height: 108,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: c.onInverse,
                  borderRadius: radius.md,
                }}
              />
              <Text style={[t.sub, { fontWeight: '600', color: c.onInverse }]}>Bank card here</Text>
            </>
          ) : null}
        </View>

        {/*
          Auto-advance. A white card at the foot of the viewfinder saying what just
          finished and what is next, with the bar running under it. "Stay" is the escape:
          an operator who wants another angle of the pane they just shot must not have to
          race a timer to get it.
        */}
        {advancing ? (
          <View
            testID="auto-advance"
            style={{
              position: 'absolute',
              left: space.sm,
              right: space.sm,
              bottom: space.sm,
              borderRadius: radius.md,
              backgroundColor: c.surface,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: space.sm,
                padding: space.sm,
                paddingLeft: space.md,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={[t.label, { color: c.text }]}>{`${AREA_LABELS[area]} done`}</Text>
                <Text style={[t.body, { color: c.dim }]}>
                  {index + 1 < areas.length
                    ? `Next: ${AREA_LABELS[areas[index + 1]]}`
                    : 'Next: check before sending'}
                </Text>
              </View>
              <OutlineButton
                testID="auto-advance-stay"
                label="Stay"
                onPress={() => setAdvancing(false)}
              />
            </View>
            <AutoAdvanceBar />
          </View>
        ) : null}

        {/*
          The undo window. Deleting is one tap on a 24 pt dot at the edge of a thumbnail,
          held in one hand, outdoors — the easiest mis-tap on the screen, and the
          photograph is gone for good once this closes.
        */}
        {undo ? (
          <View
            testID="undo-toast"
            style={{
              position: 'absolute',
              left: space.sm,
              right: space.sm,
              bottom: space.sm,
              flexDirection: 'row',
              alignItems: 'center',
              gap: space.sm,
              paddingLeft: space.md,
              paddingRight: space.sm,
              paddingVertical: space.sm,
              borderRadius: radius.md,
              backgroundColor: c.surface,
            }}
          >
            <Text style={[t.body, { flex: 1, color: c.text }]}>
              {`${SHOT_LABEL[undo.shot ?? 'wide']} photo deleted`}
            </Text>
            <PrimaryButton
              testID="undo-delete"
              label="Undo"
              onPress={() => {
                onAdd(undo);
                setUndo(null);
              }}
            />
          </View>
        ) : null}
      </View>

      <View style={{ paddingHorizontal: space.md, gap: space.sm }}>
        {shots.length > 0 ? (
          <View style={{ flexDirection: 'row', gap: space.md, paddingTop: space.sm }}>
            {shots.map((p, i) => (
              <View key={p.id} style={{ width: 64 }}>
                <Image
                  source={{ uri: p.uri }}
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: radius.md,
                    backgroundColor: c.inverseRaise,
                  }}
                />
                <Text
                  style={[
                    t.micro,
                    {
                      position: 'absolute',
                      left: space.xs,
                      bottom: space.xs,
                      fontWeight: '600',
                      color: c.onInverse,
                      backgroundColor: c.inverse,
                      borderRadius: radius.sm,
                      paddingHorizontal: space.xs,
                      overflow: 'hidden',
                    },
                  ]}
                >
                  {p.shot ? SHOT_LABEL[p.shot] : 'Photo'}
                </Text>
                <Pressable
                  testID={`remove-photo-${i}`}
                  accessibilityRole="button"
                  accessibilityLabel={`Delete photo ${i + 1}`}
                  onPress={() => {
                    setUndo(p);
                    onRemove(p.id);
                  }}
                  hitSlop={12}
                  style={{
                    position: 'absolute',
                    top: -space.sm,
                    right: -space.sm,
                    width: 24,
                    height: 24,
                    borderRadius: radius.pill,
                    backgroundColor: c.surface,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text style={[t.sub, { fontWeight: '600', color: c.text }]}>✕</Text>
                </Pressable>
              </View>
            ))}
          </View>
        ) : null}

        {error ? (
          <Notice tone="stateFailed" title="Could not take that photo.">
            Try again. Nothing already shot for this area is affected.
          </Notice>
        ) : null}
      </View>

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: space.md,
          paddingVertical: space.md,
        }}
      >
        <View style={{ width: 88 }}>
          {index > 0 ? (
            <OutlineButton
              label="Prev"
              onPress={() => setIndex(index - 1)}
              testID="shoot-prev"
              onDark
            />
          ) : null}
        </View>

        <Pressable
          testID="shutter"
          accessibilityRole="button"
          accessibilityLabel={`Photograph the ${AREA_LABELS[area]}`}
          accessibilityState={{ disabled: !canAdd || busy || !hasPermission }}
          disabled={!canAdd || busy || !hasPermission}
          onPress={() => void shoot()}
          style={{
            width: touch.shutter,
            height: touch.shutter,
            borderRadius: touch.shutter / 2,
            borderWidth: 4,
            borderColor: canAdd && hasPermission ? c.onInverse : c.inverseRule,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {busy ? (
            <ActivityIndicator color={c.onInverse} />
          ) : (
            <View
              style={{
                width: 62,
                height: 62,
                borderRadius: 31,
                backgroundColor: canAdd && hasPermission ? c.onInverse : c.inverseRule,
              }}
            />
          )}
        </Pressable>

        <View style={{ width: 88 }}>
          <OutlineButton
            testID="shoot-next"
            label={index + 1 < areas.length ? 'Next area' : 'Done'}
            disabled={shots.length === 0}
            onPress={advance}
            onDark
          />
        </View>
      </View>
    </Screen>
  );
}
